// ─── Atelier chassis ─────────────────────────────────────────────────────────
// Generated from the blueprint, in the same pass as the Go structs. These two
// cannot drift: change the blueprint, not this file.

export type ListResponse<T> = {
  items: T[];
  total: number;
  limit: number;
  offset: number;
};

export type ApiError = {
  error: { code: string; message: string; fields?: Record<string, string> };
};

export type WineColor =
  | "red"
  | "white"
  | "rose"
  | "sparkling"
  | "dessert"
  | "fortified";

export const WineColorValues: WineColor[] = ["red", "white", "rose", "sparkling", "dessert", "fortified"];

export type CellarStatus =
  | "in_stock"
  | "low"
  | "reserved"
  | "out_of_stock";

export const CellarStatusValues: CellarStatus[] = ["in_stock", "low", "reserved", "out_of_stock"];

export type User = {
  id: string;
  email: string;
  name: string;
  role: "viewer" | "member" | "admin" | "owner";
  last_active_at?: string | null;
  created_at: string;
  updated_at: string;
};

/** A wine-growing region, shared reference data for the whole restaurant. */
export type Region = {
  id: string;
  name: string;
  country: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type RegionInput = Omit<Region, "id" | "created_at" | "updated_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const RegionFields = [
  { name: "name" as const, label: "Name", type: "string", required: true },
  { name: "country" as const, label: "Country", type: "string", required: true },
  { name: "notes" as const, label: "Notes", type: "text", required: false },
];

/** A winery or producer whose bottles appear in the cellar. */
export type Producer = {
  id: string;
  name: string;
  website?: string | null;
  phone?: string | null;
  description?: string | null;
  region_id?: string;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type ProducerInput = Omit<Producer, "id" | "created_at" | "updated_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const ProducerFields = [
  { name: "name" as const, label: "Name", type: "string", required: true },
  { name: "website" as const, label: "Website", type: "url", required: false },
  { name: "phone" as const, label: "Phone", type: "phone", required: false },
  { name: "description" as const, label: "Description", type: "text", required: false },
];

/** A specific wine held in the cellar, with vintage, quantity on hand and tasting score. */
export type Bottle = {
  id: string;
  name: string;
  grape?: string | null;
  color: WineColor;
  vintage_year: number;
  quantity_on_hand: number;
  status: CellarStatus;
  tasting_score?: number | null;
  purchase_price_cents?: number | null;
  list_price_cents?: number | null;
  cellar_location?: string | null;
  acquired_on?: string | null;
  producer_id?: string;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type BottleInput = Omit<Bottle, "id" | "created_at" | "updated_at" | "deleted_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const BottleFields = [
  { name: "name" as const, label: "Name", type: "string", required: true },
  { name: "grape" as const, label: "Grape", type: "string", required: false },
  { name: "color" as const, label: "Color", type: "enum", required: true, enum: WineColorValues },
  { name: "vintage_year" as const, label: "Vintage Year", type: "int", required: true },
  { name: "quantity_on_hand" as const, label: "Quantity On Hand", type: "int", required: true },
  { name: "status" as const, label: "Status", type: "enum", required: true, enum: CellarStatusValues },
  { name: "tasting_score" as const, label: "Tasting Score", type: "int", required: false },
  { name: "purchase_price_cents" as const, label: "Purchase Price Cents", type: "money", required: false },
  { name: "list_price_cents" as const, label: "List Price Cents", type: "money", required: false },
  { name: "cellar_location" as const, label: "Cellar Location", type: "string", required: false },
  { name: "acquired_on" as const, label: "Acquired On", type: "date", required: false },
];

/** A tasting note written by a staff member about a bottle. */
export type TastingNote = {
  id: string;
  owner_id: string;
  headline: string;
  body: string;
  score?: number | null;
  tasted_on?: string | null;
  bottle_id?: string;
  created_at: string;
  updated_at: string;
};

/** The writable shape: the server assigns id, owner and timestamps. */
export type TastingNoteInput = Omit<TastingNote, "id" | "owner_id" | "created_at" | "updated_at">;

/** Column metadata, so tables and forms are described once rather than per page. */
export const TastingNoteFields = [
  { name: "headline" as const, label: "Headline", type: "string", required: true },
  { name: "body" as const, label: "Body", type: "markdown", required: true },
  { name: "score" as const, label: "Score", type: "int", required: false },
  { name: "tasted_on" as const, label: "Tasted On", type: "date", required: false },
];
